import React, { useRef, useState } from 'react';

const MAKey = ({ name, isMA3 }) => {
  const isSpecial = name === 'ma' || name === 'MA';
  const display = name === 'ma' ? 'MA' : name;
  
  // Aggressive adaptive font size to fit in a strict SQUARE
  let fontSize = 'text-[10px]';
  if (display.length > 5) fontSize = 'text-[6px]';
  else if (display.length > 4) fontSize = 'text-[7px]';
  else if (display.length > 3) fontSize = 'text-[8px]';
  else if (display.length > 2) fontSize = 'text-[9px]';

  return (
    <span className={`
      inline-flex items-center justify-center w-8 h-8 mx-0.5 align-middle
      ${fontSize} font-bold uppercase rounded-lg 
      shadow-[0_2px_4px_rgba(0,0,0,0.5)] border-2
      transition-all duration-200 flex-shrink-0 leading-none overflow-hidden
      hover:scale-105 hover:shadow-lg
      ${isMA3 
        ? 'bg-white border-gray-300 shadow-gray-200' 
        : 'bg-[#3a3a3a] border-[#555] shadow-black/80'
      }
      ${isSpecial 
        ? (isMA3 ? 'text-ma3-accent' : 'text-ma2-accent border-ma2-accent/40') 
        : (isMA3 ? 'text-gray-700' : 'text-gray-100')
      }
    `}>
      <span className="text-center px-0.5 whitespace-nowrap overflow-hidden block w-full">
        {display}
      </span>
    </span>
  );
};

const MALink = ({ name, isMA3 }) => (
  <span className={`
    inline-block px-2 py-0.5 rounded mx-0.5 text-xs font-bold border
    ${isMA3 
      ? 'bg-blue-50 border-blue-100 text-blue-600' 
      : 'bg-blue-500/10 border-blue-500/30 text-blue-400'
    }
  `}>
    {name}
  </span>
);

const renderDescriptionText = (text, isMA3) => {
  if (!text) return null;
  
  const parts = text.split(/(\[\[(?:KEY|LINK):.*?\]\])/g);
  return parts.map((part, i) => {
    const keyMatch = part.match(/\[\[KEY:(.*?)\]\]/);
    if (keyMatch) return <MAKey key={i} name={keyMatch[1]} isMA3={isMA3} />;
    
    const linkMatch = part.match(/\[\[LINK:(.*?)\]\]/);
    if (linkMatch) return <MALink key={i} name={linkMatch[1]} isMA3={isMA3} />;
    
    return part;
  });
};

const MACallout = ({ type, text, isMA3 }) => {
  const normalizedType = (type || 'NOTE').toUpperCase();
  
  // Colors aligned with the app's theme
  const themeAccent = isMA3 ? 'blue-500' : 'yellow-400';
  const themeBg = isMA3 ? 'bg-gray-100/80' : 'bg-zinc-800/80';
  const themeText = isMA3 ? 'text-gray-900' : 'text-gray-100';
  const themeLabel = isMA3 ? 'text-blue-700' : 'text-yellow-400';

  const typeIcons = {
    HINT: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" strokeLinecap="round" />
        <line x1="12" y1="8" x2="12.01" y2="8" strokeLinecap="round" />
      </svg>
    ),
    IMPORTANT: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    DEFAULT: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" strokeLinecap="round" />
        <line x1="12" y1="8" x2="12.01" y2="8" strokeLinecap="round" />
      </svg>
    )
  };

  const icon = typeIcons[normalizedType] || typeIcons.DEFAULT;
  const title = normalizedType;

  return (
    <div className={`my-6 flex flex-col gap-2 p-4 rounded-xl border-l-[4px] border-${themeAccent} ${themeBg} ${isMA3 ? 'border-gray-200 shadow-sm' : 'border-gray-800'} transition-all`}>
      <div className="flex items-center gap-2">
        <div className={themeLabel}>
          {icon}
        </div>
        <span className={`font-black uppercase text-[10px] tracking-widest ${themeLabel}`}>
          {title}
        </span>
      </div>
      <div className={`text-sm font-semibold leading-relaxed ${themeText}`}>
        {renderDescriptionText(text, isMA3)}
      </div>
    </div>
  );
};

const KeywordsPanel = ({ 
  keywordsLines, 
  keywordFilter, 
  setKeywordFilter, 
  selectedKeyword, 
  setSelectedKeyword, 
  onInsert, 
  isMA3,
  keywords = {}
}) => {
  const detailsRef = useRef(null);
  const [selectionHistory, setSelectionHistory] = useState([]);
  // Filter keywords based on the search input (starts with)
  const filteredKeywords = keywordsLines
    .filter(([kw]) => kw.toLowerCase().startsWith(keywordFilter.toLowerCase()))
    .sort(([a], [b]) => {
      if (isMA3) {
        const getSortCategory = (s) => {
          // Category 2: Options (starts with / and has more chars)
          if (s.startsWith('/') && s.length > 1) return 2;
          // Category 0: Symbols (starts with non-alphanumeric)
          if (!/^[a-zA-Z0-9]/.test(s)) return 0;
          // Category 1: Regular keywords
          return 1;
        };
        const catA = getSortCategory(a);
        const catB = getSortCategory(b);
        if (catA !== catB) return catA - catB;
      }
      return a.localeCompare(b);
    });

  return (
    <div className={`flex flex-col h-full overflow-hidden ${isMA3 ? 'bg-gray-50 text-gray-900' : 'bg-[#151515] text-white'}`}>
      {/* Header / Search */}
      <div className={`p-4 border-b ${isMA3 ? 'border-gray-200 bg-white' : 'border-gray-800 bg-[#1a1a1a]'}`}>
        <div className="flex items-center justify-between mb-2">
           <h2 className={`text-sm font-bold tracking-wider uppercase opacity-70 ${isMA3 ? 'text-gray-900' : 'text-gray-100'}`}>Library</h2>
           <span className="text-xs opacity-40">{filteredKeywords.length} items</span>
        </div>
        <div className="relative group">
          <input
            type="text"
            placeholder="Search keywords..."
            value={keywordFilter}
            onChange={(e) => setKeywordFilter(e.target.value)}
            className={`
              w-full p-2.5 pl-9 rounded-lg text-sm font-medium
              ${isMA3 
                ? 'bg-gray-100 text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-500/20' 
                : 'bg-zinc-800 text-white focus:bg-zinc-700 focus:ring-2 focus:ring-yellow-500/20'
              } 
              focus:outline-none placeholder-gray-500
            `}
          />
          <svg className="w-4 h-4 absolute left-3 top-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          
          {keywordFilter && (
             <button 
               onClick={() => setKeywordFilter("")}
               className="absolute right-2 top-2 p-1 rounded-md hover:bg-black/10 dark:hover:bg-white/10"
             >
               <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
             </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col xl-custom:flex-row overflow-hidden">
        {/* List */}
        <div className={`w-full xl-custom:w-[25%] h-[40%] xl-custom:h-auto border-b xl-custom:border-b-0 xl-custom:border-r overflow-y-auto custom-scrollbar ${isMA3 ? 'border-gray-200' : 'border-gray-800'}`}>
          {filteredKeywords.length > 0 ? (
            <ul className="divide-y divide-transparent">
              {filteredKeywords.map(([kw, data]) => (
                  <li
                    key={kw}
                    className={`
                      relative px-6 py-3 cursor-pointer text-base font-mono transition-all
                      ${selectedKeyword?.name === kw 
                        ? (isMA3 ? 'bg-blue-50 text-blue-700' : 'bg-yellow-500/10 text-yellow-400') 
                        : (isMA3 ? 'hover:bg-gray-100 text-gray-700' : 'hover:bg-white/5 text-gray-400 hover:text-gray-200')
                      }
                    `}
                    onClick={() => {
                      setSelectionHistory([]); // Clear history when selecting from list
                      setSelectedKeyword({ name: kw, ...data });
                      detailsRef.current?.scrollTo({ top: 0, behavior: 'auto' });
                    }}
                    onDoubleClick={() => onInsert(kw)}
                  >
                    {/* Active Indicator Bar */}
                    {selectedKeyword?.name === kw && (
                      <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${isMA3 ? 'bg-blue-500' : 'bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.5)]'}`} />
                    )}
                    {kw}
                  </li>
                ))}
            </ul>
          ) : (
            <div className="p-8 text-center opacity-40 text-sm">No keywords found</div>
          )}
        </div>

        {/* Details */}
        <div 
          ref={detailsRef}
          className={`flex-1 overflow-y-auto p-4 xl-custom:p-6 ${isMA3 ? 'bg-gray-50' : 'bg-[#151515]'}`}
        >
          {selectedKeyword ? (
            <div 
              key={selectedKeyword.name} 
              className="max-w-5xl mx-auto space-y-4 xl-custom:space-y-6"
            >
              {/* Header block */}
              <div 
                className="flex flex-col xl-custom:flex-row xl-custom:items-baseline justify-between gap-2 animate-slideUp opacity-0"
                style={{ animationDelay: '0ms' }}
              >
                <div className="flex items-center gap-3">
                  {selectionHistory.length > 0 && (
                    <button 
                      onClick={() => {
                        const prev = selectionHistory[selectionHistory.length - 1];
                        setSelectionHistory(prevHistory => prevHistory.slice(0, -1));
                        setSelectedKeyword(prev);
                        detailsRef.current?.scrollTo({ top: 0, behavior: 'auto' });
                      }}
                      className={`p-1.5 rounded-lg transition-colors ${
                        isMA3 ? 'text-gray-400 hover:bg-gray-100' : 'text-gray-500 hover:bg-white/5'
                      }`}
                      title="Back"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                    </button>
                  )}
                  <h3 className={`text-2xl xl-custom:text-3xl font-bold font-mono ${isMA3 ? 'text-gray-900' : 'text-white'}`}>
                    {selectedKeyword.display_name || selectedKeyword.name}
                  </h3>
                </div>
                <button 
                  onClick={() => onInsert(selectedKeyword.name)}
                  className={`text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wide transition-colors ${
                    isMA3 ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-yellow-400/20 text-yellow-400 hover:bg-yellow-400/30'
                  }`}
                >
                  Insert Keyword
                </button>
              </div>

              {/* Description block */}
              {selectedKeyword.description && (
                <div 
                  className={`space-y-3 p-4 rounded-xl border animate-slideUp opacity-0 ${isMA3 ? 'bg-white border-gray-200 text-gray-600' : 'bg-[#222] border-gray-800 text-gray-300'}`}
                  style={{ animationDelay: '100ms' }}
                >
                  {Array.isArray(selectedKeyword.description) ? (
                    selectedKeyword.description.map((para, idx) => {
                      // Detect Callout Box (Robust regex to handle variations in parsing)
                      const calloutMatch = para.match(/^\[\[CALLOUT:(.*?):(.*?)\]\]$/s) || 
                                           para.match(/^\[\[CALLOUT:(.*?)\]\]$/s);
                      
                      if (calloutMatch) {
                        const type = calloutMatch[2] ? calloutMatch[1] : 'NOTE';
                        const text = calloutMatch[2] || calloutMatch[1];
                        return <MACallout key={idx} type={type} text={text} isMA3={isMA3} />;
                      }

                      // Detect Markdown Table
                      if (para.trim().startsWith('|') && para.includes('\n|')) {
                        const rows = para.split('\n').filter(r => r.trim().startsWith('|'));
                        // Extract cells, filtering out empty first/last parts from split
                        const tableData = rows.map(r => r.split('|').map(c => c.trim()).filter((c, i, a) => i > 0 && i < a.length - 1));
                        
                        let headers = null;
                        let bodyRows = tableData;
                        
                        // Check for separator row (---|---)
                        if (rows.length >= 2 && rows[1].includes('---')) {
                          headers = tableData[0];
                          bodyRows = tableData.slice(2);
                        }

                        return (
                          <div key={idx} className={`my-4 overflow-x-auto rounded-lg border ${isMA3 ? 'border-gray-100' : 'border-gray-800'}`}>
                            <table className="w-full text-xs xl-custom:text-sm border-collapse">
                              {headers && (
                                <thead className={isMA3 ? 'bg-gray-50' : 'bg-white/5'}>
                                  <tr>
                                    {headers.map((h, i) => (
                                      <th key={i} className={`px-3 py-2 text-center font-bold border-r last:border-r-0 ${isMA3 ? 'text-gray-900 border-gray-100' : 'text-gray-100 border-white/5'}`}>
                                        {renderDescriptionText(h, isMA3)}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                              )}
                              <tbody className={`divide-y ${isMA3 ? 'divide-gray-50' : 'divide-white/5'}`}>
                                {bodyRows.map((row, i) => (
                                  <tr key={i} className="hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                                    {row.map((cell, j) => (
                                      <td key={j} className={`px-3 py-2 text-center border-r last:border-r-0 ${isMA3 ? 'border-gray-100' : 'border-white/5'}`}>
                                        {renderDescriptionText(cell, isMA3)}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      }

                      return (
                        <p key={idx} className="leading-relaxed text-sm">
                          {renderDescriptionText(para, isMA3)}
                        </p>
                      );
                    })
                  ) : (
                    <p className="leading-relaxed text-sm">
                      {renderDescriptionText(String(selectedKeyword.description), isMA3)}
                    </p>
                  )}
                </div>
              )}

              {/* Syntax block */}
              {selectedKeyword.syntax && (Array.isArray(selectedKeyword.syntax) ? selectedKeyword.syntax.length > 0 : String(selectedKeyword.syntax).trim() !== "") && (
                <div className="animate-slideUp opacity-0" style={{ animationDelay: '200ms' }}>
                  <h4 className={`text-xs font-bold uppercase tracking-widest mb-3 opacity-50 ${isMA3 ? 'text-gray-900' : 'text-white'}`}>Syntax</h4>
                  <div className={`
                    font-mono p-4 rounded-xl border border-opacity-10
                    ${isMA3 
                      ? 'bg-gray-100 text-gray-800 border-gray-900' 
                      : 'bg-black text-green-400 border-white'
                    }
                  `}>
                     {Array.isArray(selectedKeyword.syntax) ? (
                        <ul className="space-y-2">
                          {selectedKeyword.syntax.map((s, i) => (
                            <li key={i}>
                              <span className="font-bold">{typeof s === 'object' ? JSON.stringify(s) : String(s)}</span>
                            </li>
                          ))}
                        </ul>
                     ) : (
                        <p className="font-bold">{typeof selectedKeyword.syntax === 'object' ? JSON.stringify(selectedKeyword.syntax) : String(selectedKeyword.syntax)}</p>
                     )}
                  </div>
                </div>
              )}

              {/* Values block (specifically for options) */}
              {Array.isArray(selectedKeyword.values) && selectedKeyword.values.length > 0 && (
                <div className="animate-slideUp opacity-0" style={{ animationDelay: '250ms' }}>
                  <h4 className={`text-xs font-bold uppercase tracking-widest mb-3 opacity-50 ${isMA3 ? 'text-gray-900' : 'text-white'}`}>Values</h4>
                  
                  {selectedKeyword.values.some(v => v.description && v.description.trim() !== '') ? (
                    <div className={`overflow-hidden rounded-xl border ${isMA3 ? 'border-gray-100 bg-gray-50/30' : 'border-white/5 bg-white/[0.02]'}`}>
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className={isMA3 ? 'bg-gray-100/50' : 'bg-white/5'}>
                            <th className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider ${isMA3 ? 'text-gray-400' : 'text-white/30'} w-1/4`}>Value</th>
                            <th className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider ${isMA3 ? 'text-gray-400' : 'text-white/30'}`}>Description</th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${isMA3 ? 'divide-gray-100' : 'divide-white/5'}`}>
                          {selectedKeyword.values.map((val, i) => (
                            <tr key={i} className="hover:bg-white/[0.01] transition-colors">
                              <td className={`px-4 py-3 text-xs font-bold ${isMA3 ? 'text-blue-600' : 'text-yellow-400'} align-middle`}>
                                {typeof val === 'object' ? val.name : val}
                              </td>
                              <td className={`px-4 py-3 text-xs leading-relaxed align-middle ${isMA3 ? 'text-gray-600' : 'text-gray-400'}`}>
                                {typeof val === 'object' ? val.description : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedKeyword.values.map((val, i) => (
                        <span key={i} className={`
                          px-3 py-1.5 rounded-lg text-xs font-bold border border-opacity-20
                          ${isMA3 
                            ? 'bg-blue-50 text-blue-700 border-blue-400' 
                            : 'bg-yellow-400/10 text-yellow-400 border-yellow-400'
                          }
                        `}>
                          {typeof val === 'object' ? val.name : val}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Examples block */}
              {Array.isArray(selectedKeyword.examples) && selectedKeyword.examples.length > 0 && (
                <div className="animate-slideUp opacity-0" style={{ animationDelay: '300ms' }}>
                  <h4 className={`text-xs font-bold uppercase tracking-widest mb-3 opacity-50 ${isMA3 ? 'text-gray-900' : 'text-white'}`}>Examples</h4>
                  <div className={`font-mono text-sm p-4 rounded-lg bg-opacity-50 ${isMA3 ? 'bg-gray-200 text-gray-800' : 'bg-black text-blue-400'}`}>
                    <ul className="space-y-2">
                      {selectedKeyword.examples.map((ex, i) => (
                        <li key={i} className="flex gap-2">
                           <span className="opacity-50 select-none">$</span>
                           <span>{typeof ex === 'object' ? JSON.stringify(ex) : String(ex)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Options Table block */}
              {Array.isArray(selectedKeyword.options) && selectedKeyword.options.length > 0 && (
                <div className="animate-slideUp opacity-0 pb-12" style={{ animationDelay: '400ms' }}>
                  <h4 className={`text-xs font-bold uppercase tracking-widest mb-3 opacity-50 ${isMA3 ? 'text-gray-900' : 'text-white'}`}>Options</h4>
                  <div className={`rounded-xl border border-collapse overflow-hidden ${isMA3 ? 'bg-white border-gray-200' : 'bg-[#1a1a1a] border-gray-800'}`}>
                    <table className="w-full text-left table-auto">
                      <thead>
                          <tr className={isMA3 ? 'bg-gray-100/50' : 'bg-white/5'}>
                            <th className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider ${isMA3 ? 'text-gray-400' : 'text-white/30'}`}>Command / Short</th>
                            <th className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider ${isMA3 ? 'text-gray-400' : 'text-white/30'} ${isMA3 ? 'text-center' : 'text-left'}`}>Values</th>
                            <th className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider ${isMA3 ? 'text-gray-400' : 'text-white/30'}`}>Description</th>
                          </tr>
                      </thead>
                      <tbody className={`divide-y ${isMA3 ? 'divide-gray-100' : 'divide-white/5'}`}>
                        {selectedKeyword.options.map((opt, i) => (
                          <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                            <td className="px-3 py-3 align-middle">
                              {(() => {
                                const name = opt.option.startsWith('/') ? opt.option : '/' + opt.option;
                                const targetKw = isMA3 ? (keywords[name] || keywords[opt.option]) : null;
                                const hasLink = !!targetKw;
                                
                                return (
                                  <div 
                                    className={`font-mono font-bold text-xs outline-none text-left mb-0.5 ${
                                      isMA3 
                                        ? (hasLink ? 'text-blue-600 cursor-pointer hover:underline' : 'text-gray-500 cursor-default opacity-80') 
                                        : 'text-yellow-400'
                                    }`}
                                    onClick={() => {
                                      if (!isMA3 || !hasLink) return;
                                      
                                      setSelectionHistory(prev => [...prev, selectedKeyword]);
                                      setSelectedKeyword({ name: targetKw.name || name, ...targetKw });
                                      detailsRef.current?.scrollTo({ top: 0, behavior: 'auto' });
                                    }}
                                  >
                                    {opt.option.startsWith('/') ? opt.option.substring(1) : opt.option}
                                  </div>
                                );
                              })()}
                              {opt.shortcut && (
                                <div className={`font-mono text-[10px] opacity-40`}>
                                  {opt.shortcut}
                                </div>
                              )}
                            </td>
                            <td className={`px-3 py-3 align-middle ${isMA3 ? 'text-center' : 'text-left'} w-[120px]`}>
                              {opt.values && opt.values.trim() !== '' && opt.values !== 'no option value' ? (
                                <div className={`flex flex-wrap ${isMA3 ? 'justify-center' : 'justify-start'} gap-1`}>
                                  {opt.values.split(',').map((val, idx) => (
                                    <span 
                                      key={idx}
                                      className={`text-[8px] px-1.5 py-0.5 rounded bg-opacity-10 font-medium ${
                                        isMA3 ? 'bg-gray-600 text-gray-500' : 'bg-white text-gray-400'
                                      }`}
                                    >
                                      {val.trim()}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className={`opacity-20 text-[9px]`}>—</span>
                              )}
                            </td>
                            <td className={`px-3 py-3 text-xs leading-relaxed align-middle ${isMA3 ? 'text-gray-600' : 'text-gray-400'}`}>
                              {opt.description}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={`h-full flex flex-col items-center justify-center select-none ${isMA3 ? 'text-gray-400 opacity-60' : 'opacity-20'}`}>
              <svg className="w-24 h-24 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
              <p>Select a keyword to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KeywordsPanel;
